import { useState, useEffect, useCallback } from 'react';
import { Loading, NavMenu, Pagination } from '../../components';
import { getOnlineShops } from '../../services/shopsServices';
import { useErrorsContext } from '../../contexts/ErrorsContext.js';
import { useAuthContext } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { SupportTypeEnum } from '../../utils/SupportTypeEnum';
import './OnlineShops.scss';

const OnlineShops = ({ fromPublic, supportType }) => {

    const { t } = useTranslation();
    const { showError } = useErrorsContext();
    const { petitionSupportType } = useAuthContext();
    const effectiveSupportType = fromPublic ? supportType : petitionSupportType;
    const [loading, setLoading] = useState(true);
    const [merchants, setMerchants] = useState();
    const [value, setValue] = useState();
    const [type, setType] = useState();
    const [shopSelected, setShopSelected] = useState();
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const pageSize = 10;

    const getShops = useCallback(async () => {
        try {
            setLoading(true);
            const searchParams = new URLSearchParams()
            searchParams.append('page', page);
            searchParams.append('pageSize', pageSize);
            if (value) searchParams.append('search', value);
            if (type) searchParams.append('wallet', type);
            if (effectiveSupportType) searchParams.append('supportType', effectiveSupportType);
            const response = await getOnlineShops(searchParams);
            const json = await response.json()
            setTotalPages(json.totalPages);
            setMerchants(json.content);
        } catch (error) {
            showError(error);
        } finally {
            setLoading(false);
        }
    }, [page, value, type, effectiveSupportType]); // eslint-disable-line

    useEffect(() => {
        getShops();
    }, [page, type, effectiveSupportType]) // eslint-disable-line

    const merchantItem = (data, index) => {
        return (
            <button
                type="button"
                className="OnlineShops__content__list__item"
                key={index}
                onClick={() => setShopSelected(data)}
                aria-label={`${t('Globals.seeCompany')} ${data?.name}`}
            >
                <div className="OnlineShops__content__list__item__right">
                    <h2 className="OnlineShops__content__list__item__right__name">
                        {data?.name || "-"}
                    </h2>
                    <p className="OnlineShops__content__list__item__right__web">
                        {data?.webAddress}
                    </p>
                    {data?.subCategories?.split(',').map((item, index) => (
                        <p className="OnlineShops__content__list__item__right__types d-block" key={index}>
                            - {t(`Globals.merchantType.${item.trim()}`)}.
                        </p>
                    ))}
                </div>
                <div className="OnlineShops__content__list__item__left">
                    <div className="OnlineShops__content__list__item__left__image">
                        <img alt=' ' src={data?.image || 'misc/icons/logo-bono.png'} />
                    </div>
                </div>
            </button>
        )
    }

    const getCompleteWeb = (url) => {
        if(!url.includes("http")) {
            return `https://${url}`;
        }
        return url;
    }

    const details = () => (
        <div className="OnlineShops__content__shopDetail">
            <div className="OnlineShops__content__shopDetail__container">
                <div className="OnlineShops__content__shopDetail__container__button-back-container">
                    <button
                        type="button"
                        className="OnlineShops__content__shopDetail__container__button-back-container__button-back"
                        onClick={() => {setShopSelected()}}
                    >
                        <span className="sr-only">{t('Globals.return')}</span>
                        <img src="/misc/icons/arrow-left-pink.svg" alt=" " />
                    </button>
                </div>
                <div className="OnlineShops__content__shopDetail__container__content-container">
                    <div className="OnlineShops__content__shopDetail__container__content-container__image-container">
                        <div className="OnlineShops__content__shopDetail__container__content-container__image-container__image">
                            <img alt='icon' src={shopSelected?.image || 'misc/icons/logo-bono.png'} />
                        </div>
                    </div>
                    <h1 className="OnlineShops__content__shopDetail__container__content-container__title">
                        {shopSelected?.name}
                    </h1>
                    { shopSelected?.webAddress && (
                        <a
                            className="OnlineShops__content__shopDetail__container__content-container__web"
                            href={getCompleteWeb(shopSelected?.webAddress)}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {shopSelected?.webAddress}
                        </a>
                    )}
                    {shopSelected?.subCategories?.split(',').map((item, index) => (
                        <p className="OnlineShops__content__shopDetail__container__content-container__category" key={index}>
                        - {t(`Globals.merchantType.${item.trim()}`)}.
                        </p>
                    ))}
                </div>
            </div>
        </div>
    )

    const changeFilter = (selected) => {
        setType(selected);
    }

    const content = (
        <div className={`OnlineShops ${fromPublic ? 'fromPublic' : ''}`}>
            <div className={`OnlineShops__content`}>
                {shopSelected ? (
                    details()
                ) : (
                    loading ? (
                      <div className='vh-100 d-flex justify-content-center align-items-center'>
                          <Loading />
                      </div>
                    ) : (
                        <>
                            {!fromPublic && <div className="w-100 d-flex justify-content-center">
                                <div className="OnlineShops__content__title">
                                    <h1 className="text-center">{t('Shops.onlineSales')}</h1>
                                </div>
                            </div>}
                            <div className={`OnlineShops__content__search`}>
                                <input
                                    type="text" placeholder={t('Shops.searchShop')}
                                    value={value}
                                    onChange={(event) => {
                                        setValue(event.target.value);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            !page ? getShops() : setPage(0);
                                        }
                                    }}
                                />
                                <button
                                    type="button"
                                    className="icon button-only-icon"
                                    onClick={() => !page ? getShops() : setPage(0) }
                                >
                                    <span className="sr-only">{t('Globals.search')}</span>
                                    <img src="/misc/icons/search.svg" alt=" " />
                                </button>
                            </div>
                            {effectiveSupportType !== SupportTypeEnum.CREATOR && (
                                <div className='OnlineShops__content__chips'>
                                    <button type="button" className={`OnlineShops__content__chips__item ${!type ? 'selected' : ''}`} onClick={() => changeFilter(null)}>{t('Shops.filters.all')}</button>
                                    <button type="button" className={`OnlineShops__content__chips__item ${type == 'LIVE_ARTS' ? 'selected' : ''}`} onClick={() => changeFilter('LIVE_ARTS')}>{t('Shops.filters.cultural')}</button>
                                    <button type="button" className={`OnlineShops__content__chips__item ${type == 'DIGITAL_SUPPORT' ? 'selected' : ''}`} onClick={() => changeFilter('DIGITAL_SUPPORT')}>{t('Shops.filters.digital')}</button>
                                    <button type="button" className={`OnlineShops__content__chips__item ${type == 'PHYSICAL_SUPPORT' ? 'selected' : ''}`} onClick={() => changeFilter('PHYSICAL_SUPPORT')}>{t('Shops.filters.physical')}</button>
                                </div>
                            )}
                            <div className='OnlineShops__content__list'>
                                {merchants?.length > 0 ? (
                                    merchants?.map((item, index) => (
                                        merchantItem(item, index)
                                    ))
                                ): (
                                    <p className='OnlineShops__content__list__empty'>No se han encontrado empresas de venta online</p>
                                )}
                            </div>
                            <Pagination
                                page={page}
                                totalPages={totalPages}
                                onPageChange={(selected) => {
                                    setPage(selected);
                                }}
                            />
                        </>
                    )
                )}
            </div>
        </div>  
    )

    return (
        fromPublic ? (
            content
        ) : (
            <NavMenu>
                {content}    
            </NavMenu>
        )
    );
};

export default OnlineShops;
